import type { InputHTMLAttributes, ReactNode } from 'react';
import { useState } from 'react';

import { FormField } from './FormField.js';

/**
 * Password input with a visible/hidden toggle. Defaults to hidden.
 * The toggle button is keyboard-accessible and has its own
 * aria-label.
 *
 * Behaviour: the toggle does NOT persist its state across page
 * reloads — by design, every fresh navigation starts hidden.
 *
 * Use:
 *
 *   <PasswordInput
 *     label="Mot de passe"
 *     value={password}
 *     onChange={(e) => setPassword(e.target.value)}
 *     helper="Minimum 12 caractères"
 *     error={errors.password}
 *     autoComplete="current-password"
 *     required
 *   />
 */
type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label: string;
  helper?: ReactNode;
  error?: string;
};

export function PasswordInput({ label, helper, error, className, ...inputProps }: Props) {
  const [visible, setVisible] = useState(false);
  return (
    <FormField
      label={label}
      helper={helper}
      error={error}
      renderInput={({ id, describedBy, invalid }) => (
        <div className="relative">
          <input
            {...inputProps}
            id={id}
            type={visible ? 'text' : 'password'}
            aria-describedby={helper || error ? describedBy : undefined}
            aria-invalid={invalid || undefined}
            className={
              'input-base pr-24 ' +
              (invalid ? 'border-semantic-error focus:border-semantic-error ' : '') +
              (className ?? '')
            }
          />
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            aria-pressed={visible}
            className="absolute top-1/2 right-2 -translate-y-1/2 px-2 py-1 text-xs text-ink-secondary hover:text-ink rounded-card hover:bg-platinum"
          >
            {visible ? 'Masquer' : 'Afficher'}
          </button>
        </div>
      )}
    />
  );
}
